import React from 'react';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { errorMessage } from 'constants/CommonConstants';
import { EMAIL_REGEX, PHONE_REGEX } from 'constants/Regex';
import { formPatternRules, formRequiredRules } from 'helper';
import { StepperFromProps } from 'sharedComponents/dynamicStepperForm/dynamicStepperFormInterface';
import { CheckboxInput, CommonInput, SelectInput } from 'sharedComponents/inputGroup';
import { CommonPhoneInput } from 'sharedComponents/inputGroup/CommonPhoneInput';
import { CommonTextAreaInput } from 'sharedComponents/inputGroup/CommonTextAreaInput';
import { RootState } from 'store/store';
import { InquiringOptions, timeOptions } from '../ContactUsConstant';

const Step1: React.ForwardRefRenderFunction<unknown, StepperFromProps> = () => {
  const { t } = useTranslation();

  const { authenticateClientData } = useSelector((state: RootState) => state.auth);

  const dealershipName = authenticateClientData.data?.widgetData?.dealershipName;
  const { control, trigger, watch } = useFormContext();

  const inquiringWatcher = watch('inquiring');
  const bestTimeWatcher = watch('bestTime');

  return (
    <>
      <p className='text-center'>Contact Us</p>
      <div className='two-col'>
        <div className='form-group'>
          <CommonInput
            name='firstName'
            control={control}
            label={t('stepperForm.firstName')}
            placeholder={t('stepperForm.enterFirstName')}
            rules={formRequiredRules(errorMessage(t).firstNameRequired)}
            triggerValidationForField={trigger}
          />
        </div>
        <div className='form-group'>
          <CommonInput
            name='lastName'
            control={control}
            label={t('stepperForm.lastName')}
            placeholder={t('stepperForm.enterLastName')}
            rules={formRequiredRules(errorMessage(t).lastNameRequired)}
            triggerValidationForField={trigger}
          />
        </div>
        <div className='form-group'>
          <CommonPhoneInput
            name='phone'
            control={control}
            label={t('stepperForm.mobileNumber')}
            placeholder={t('stepperForm.enterNumber')}
            rules={{
              ...formRequiredRules(errorMessage(t).phoneRequired),
              ...formPatternRules(PHONE_REGEX, errorMessage(t).invalidPhoneFormat),
            }}
            triggerValidationForField={trigger}
          />
        </div>
        <div className='form-group'>
          <CommonInput
            name='email'
            control={control}
            label={t('stepperForm.emailEg')}
            placeholder={t('stepperForm.enterEmail')}
            rules={{
              ...formRequiredRules(errorMessage(t).emailRequired),
              ...formPatternRules(EMAIL_REGEX, errorMessage(t).invalidEmailFormat),
            }}
            triggerValidationForField={trigger}
          />
        </div>

        <div className='form-group'>
          <SelectInput
            name='inquiring'
            control={control}
            label={t('contactUs.inquiring')}
            rules={formRequiredRules(errorMessage(t).required)}
            options={InquiringOptions}
            triggerValidationForField={trigger}
            defaultValue={inquiringWatcher}
          />
        </div>

        <div className='form-group'>
          <SelectInput
            name='bestTime'
            control={control}
            label={t('contactUs.bestTime')}
            rules={formRequiredRules(errorMessage(t).required)}
            options={timeOptions}
            triggerValidationForField={trigger}
            defaultValue={bestTimeWatcher}
          />
        </div>
      </div>

      <div className='form-group'>
        <CommonTextAreaInput
          name='message'
          control={control}
          label={t('checkAvailability.yourMessage')}
          rules={{
            ...formRequiredRules(errorMessage(t).required),
          }}
          triggerValidationForField={trigger}
        />
      </div>

      <div className='form-group'>
        <CheckboxInput
          name='termsAndCondition'
          control={control}
          label={t('checkAvailability.checkBoxNote', { dealershipName })}
          rules={formRequiredRules(errorMessage(t).termsAndConditionRequired)}
          triggerValidationForField={trigger}
        />
      </div>
    </>
  );
};

const StepperForm1 = React.forwardRef(Step1);

export default StepperForm1;
